import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Textarea from '@amzn/awsui-components-core/textarea';
import Box from '@amzn/awsui-components-core/box';
import { useTranslate } from 'src/hooks/useTranslate';
import { FormMode } from '../../types';

export interface SuspendedProductsSectionProps {
    /** List of suspended ASINs */
    asins: string[];
    /** Current form mode — 'view' renders read-only, 'modify' renders editable textarea */
    mode: FormMode;
    /** Comma-separated ASIN string for modify mode */
    suspendedAsins?: string;
    /** Callback to update the suspended ASIN string in modify mode */
    setSuspendedAsins?: (value: string) => void;
    /** Inline validation error message for the suspended ASINs field */
    errorText?: string;
}

/**
 * Suspended products section — displays suspended ASINs.
 * In view mode, renders a comma-separated read-only ASIN list.
 * In modify mode, renders an editable textarea (same pattern as AsinInclusionSection).
 * Shared between View and Modify pages.
 * Owns its own i18n bundle (`organisms.suspendedProductsSection`).
 */
const SuspendedProductsSection: FC<SuspendedProductsSectionProps> = ({
    asins,
    mode,
    suspendedAsins,
    setSuspendedAsins,
    errorText,
}) => {
    const { translate } = useTranslate('organisms.suspendedProductsSection');
    const isViewMode = mode === 'view';

    return (
        <Container
            header={<Header variant="h2">{translate('section_title')}</Header>}
        >
            {isViewMode ? (
                <FormField label={translate('asins_label')}>
                    <Box data-testid="suspended-asins-readonly">
                        {asins.join(', ')}
                    </Box>
                </FormField>
            ) : (
                <FormField
                    label={translate('asins_label')}
                    description={translate('asins_description')}
                    errorText={errorText}
                    i18nStrings={{ errorIconAriaLabel: 'Error' }}
                >
                    <Textarea
                        value={suspendedAsins ?? ''}
                        onChange={({ detail }) => setSuspendedAsins?.(detail.value)}
                        placeholder={translate('asins_placeholder')}
                        rows={4}
                        disabled={false}
                        data-testid="suspended-asins-textarea"
                        ariaRequired={false}
                        invalid={!!errorText}
                    />
                </FormField>
            )}
        </Container>
    );
};

export default SuspendedProductsSection;
